import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  IconButton,
  Grid,
  CardContent,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { ReturnEligibilityResponse, ReturnFormData } from '../../../../types/sales.types';
import { DecimalNumberInput } from '../../../ui';
import {
  DialogZoomTransition,
  DIALOG_ZOOM_MS,
  dialogZoomSlotProps,
  dialogZoomDialogSx,
  dialogZoomPaperSx,
} from '../../../ui/DialogZoomTransition';
import { useReturnOrderModal } from './ReturnOrderModal.hooks';
import { colors } from '../../../../styles/theme';
import { FlagSwitch } from '../../../../components/ui';
import { CloseConfirmationModal } from '../../../modals/confirmation';
import {
  CancelButton,
  SubmitButton,
  DashboardCardBlue,
  DashboardCardRed,
  HeroRefundCard,
} from './ReturnOrderModal.styles';

export interface ReturnOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ReturnFormData) => void;
  eligibility: ReturnEligibilityResponse | null;
  /** Código de orden para el encabezado (opcional). */
  orderCode?: string;
  loading: boolean;
}

const SectionLabel: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
  <Box sx={{ mb: 2, mt: 0.5, ...sx }}>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        fontSize: '0.72rem',
        color: 'text.secondary',
      }}
    >
      {children}
    </Typography>
    <Divider sx={{ mt: 1 }} />
  </Box>
);

const ReturnOrderModal: React.FC<ReturnOrderModalProps> = ({
  open,
  onClose,
  onSubmit,
  eligibility,
  orderCode,
  loading,
}) => {
  const {
    formData,
    useCustomAmount,
    customAmount,
    refundableAmount,
    adjustment,
    finalRefundAmount,
    isValid,
    handleCustomAmountToggle,
    handleCustomAmountChange,
    handleNotesChange,
    handleSubmit,
    formatCurrency,
  } = useReturnOrderModal({ open, eligibility, onSubmit });

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const hasDataEntered = useMemo(
    () => useCustomAmount || (formData.returnNotes?.trim() ?? '').length > 0,
    [formData.returnNotes, useCustomAmount],
  );

  useEffect(() => {
    if (!open) setShowCloseConfirmation(false);
  }, [open]);

  const handleCloseClick = useCallback(() => {
    if (loading) return;
    if (hasDataEntered) {
      setShowCloseConfirmation(true);
      return;
    }
    onClose();
  }, [hasDataEntered, loading, onClose]);

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirmation(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirmation(false);
  }, []);

  if (!eligibility) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseClick}
        maxWidth={false}
        fullWidth
        TransitionComponent={DialogZoomTransition}
        transitionDuration={DIALOG_ZOOM_MS}
        slotProps={dialogZoomSlotProps}
        sx={dialogZoomDialogSx}
        PaperProps={{
          sx: (theme) =>
            dialogZoomPaperSx({
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.55)'
                  : '0 20px 60px rgba(2, 6, 23, 0.18)',
              maxHeight: '92vh',
              minHeight: { xs: 'auto', sm: 520 },
              width: '100%',
              maxWidth: { xs: 'calc(100% - 24px)', sm: 1024 },
              margin: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }),
        }}
      >
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
        >
          <DialogTitle
            sx={{
              padding: '16px 24px',
              borderBottom: 1,
              borderColor: 'divider',
              background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
              color: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: 600,
              m: 0,
              flexShrink: 0,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: '#ffffff',
                  fontSize: '1.25rem',
                  lineHeight: 1.3,
                }}
              >
                Devolución de venta:
              </Typography>
              {orderCode ? (
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    color: '#ffffff',
                    fontSize: '1.05rem',
                    lineHeight: 1.35,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    opacity: 0.98,
                  }}
                >
                  {orderCode}
                </Typography>
              ) : null}
            </Box>
            <IconButton
              onClick={handleCloseClick}
              disabled={loading}
              sx={{
                color: '#ffffff',
                flexShrink: 0,
                '&:hover': {
                  color: colors.grey[200],
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={(theme) => ({
              pt: 2.5,
              pb: 2,
              px: 3,
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              backgroundColor: theme.palette.background.default,
            })}
          >
            <Box sx={{ mb: 2.5 }}>
              <HeroRefundCard variant="outlined">
                <CardContent sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Avatar
                      variant="rounded"
                      sx={(theme) => ({
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.success.main, 0.22)
                            : 'rgba(16,185,129,0.16)',
                        color: 'success.light',
                        width: 48,
                        height: 48,
                      })}
                    >
                      <AttachMoneyIcon />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="overline"
                        sx={{ letterSpacing: 0.5, color: 'success.light' }}
                      >
                        Monto a reembolsar (resultado)
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={(theme) => ({
                          fontWeight: 800,
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.success.light
                              : theme.palette.success.dark,
                          lineHeight: 1.15,
                          fontSize: { xs: '1.5rem', sm: '2rem' },
                        })}
                      >
                        {formatCurrency(finalRefundAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </HeroRefundCard>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <DashboardCardBlue variant="outlined">
                  <CardContent sx={{ py: 1.75, px: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar
                        variant="rounded"
                        sx={(theme) => ({
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.main, 0.22)
                              : 'rgba(59,130,246,0.16)',
                          color: 'primary.light',
                          width: 32,
                          height: 32,
                        })}
                      >
                        <CheckIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            letterSpacing: 0.3,
                            color: 'primary.light',
                            fontWeight: 600,
                            display: 'block',
                          }}
                        >
                          Monto reembolsable
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={(theme) => ({
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color:
                              theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.dark,
                          })}
                        >
                          {formatCurrency(eligibility.totalPaid)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </DashboardCardBlue>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DashboardCardRed variant="outlined">
                  <CardContent
                    sx={{ py: 1.75, px: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={(theme) => ({
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.error.main, 0.22)
                            : 'rgba(239,68,68,0.16)',
                        color: 'error.light',
                        width: 32,
                        height: 32,
                      })}
                    >
                      <ErrorIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ letterSpacing: 0.3, color: 'error.light', fontWeight: 600 }}
                      >
                        Monto reembolsado
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={(theme) => ({
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.error.light
                              : theme.palette.error.dark,
                        })}
                      >
                        {formatCurrency(refundableAmount)}
                      </Typography>
                    </Box>
                  </CardContent>
                </DashboardCardRed>
              </Grid>
            </Grid>

            <SectionLabel sx={{ mt: 1 }}>Monto del reembolso</SectionLabel>

            <Box sx={{ mb: 2 }}>
              <FlagSwitch
                checked={useCustomAmount}
                onChange={(e, checked) => handleCustomAmountToggle(checked)}
                showLabel
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: useCustomAmount ? 'text.primary' : 'text.secondary',
                      fontWeight: useCustomAmount ? 600 : 400,
                    }}
                  >
                    Monto personalizado
                  </Typography>
                }
              />
            </Box>

            {useCustomAmount && (
              <Box sx={{ mb: 3 }}>
                <DecimalNumberInput
                  fullWidth
                  label="Monto personalizado"
                  value={customAmount || ''}
                  onChange={(val) => handleCustomAmountChange(val != null ? String(val) : '')}
                  decimalPlaces={2}
                  min={0.01}
                  max={refundableAmount}
                  error={!isValid}
                  helperText={
                    !isValid
                      ? `El monto debe ser entre ${formatCurrency(0.01)} y ${formatCurrency(refundableAmount)}`
                      : `Máximo: ${formatCurrency(refundableAmount)}`
                  }
                  slotProps={{
                    inputLabel: {
                      sx: (theme) => ({
                        // Misma base que DialogContent: sin esto el borde outlined cruza la etiqueta
                        backgroundColor: theme.palette.background.default,
                        px: 0.75,
                        zIndex: 1,
                      }),
                    },
                  }}
                  sx={{ mb: 2 }}
                  size="small"
                />

                {isValid && adjustment > 0 && (
                  <Box
                    sx={(theme) => ({
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.06)
                          : theme.palette.grey[50],
                      border: `1px solid ${theme.palette.divider}`,
                    })}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'block', mb: 1.5, fontWeight: 600 }}
                    >
                      Resumen del ajuste
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Original
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(refundableAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Personalizado
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatCurrency(customAmount)}
                      </Typography>
                    </Box>
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: 1.5,
                        mt: 1.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      })}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Ajuste
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                        -{formatCurrency(adjustment)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <SectionLabel sx={{ mt: 2 }}>Notas</SectionLabel>

            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Motivo de la devolución..."
              value={formData.returnNotes}
              onChange={(e) => handleNotesChange(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </DialogContent>

          <DialogActions
            sx={(theme) => ({
              p: 2,
              flexShrink: 0,
              gap: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: theme.palette.background.paper,
              justifyContent: 'flex-end',
            })}
          >
            <CancelButton variant="tonal" onClick={handleCloseClick} disabled={loading}>
              Cancelar
            </CancelButton>
            <SubmitButton
              type="submit"
              variant="contained"
              disabled={loading || !isValid}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
            >
              {loading ? 'Procesando...' : 'Procesar devolución'}
            </SubmitButton>
          </DialogActions>
        </form>
      </Dialog>

      <CloseConfirmationModal
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </>
  );
};

export default ReturnOrderModal;
